import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getFetcher, postFetcher } from 'api/swr'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { Textarea } from '@/components/ui/textarea'
import Loader from '@/components/ui/Loader'
import { useToast } from '@/components/ui/use-toast'
import { cdnUrl } from 'commons/cdn'
import { MyImage } from 'components/MyImage'
import {
  TagPickerDialog,
  getCommonTags,
} from 'components/template-tags'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { TagsIcon } from 'lucide-react'

/** 与模版列表 columnTags 一致：非全小写视为一级，全小写视为二级 */
const splitTagsByLevel = (tags: string[] = []) => {
  const level1: string[] = []
  const level2: string[] = []
  for (const tag of tags) {
    if (tag === tag.toLowerCase()) level2.push(tag)
    else level1.push(tag)
  }
  return { level1, level2 }
}

const TagBadges = ({
  tags,
  variant,
}: {
  tags: string[]
  variant: 'default' | 'outline'
}) => {
  if (!tags.length) return null
  return (
    <>
      {tags.map((tag) => (
        <Badge variant={variant} key={tag}>
          <small>{tag}</small>
        </Badge>
      ))}
    </>
  )
}

type PreviewFrame = {
  id: string
  name?: string
  cover?: string
  tags: string[]
  online: boolean
  missing: boolean
}

type PreviewResult = {
  frames: PreviewFrame[]
  commonTags: string[]
  totals: {
    requested: number
    found: number
    online: number
    missing: number
  }
}

type BatchJob = {
  id: string
  status: 'running' | 'done' | 'failed'
  add: string[]
  remove: string[]
  totals: { frames: number; ops: number }
  done: number
  failed: number
  skipped: number
  current?: string
  errors: Array<{ id: string; op: string; message: string }>
}

const parseIds = (raw: string) => {
  const seen = new Set<string>()
  const ids: string[] = []
  for (const part of raw.split(/[\s,，;；]+/)) {
    const id = part.trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

const Help = () => (
  <HoverCard openDelay={200}>
    <HoverCardTrigger asChild>
      <button
        type="button"
        className="inline-flex shrink-0 rounded-full text-muted-foreground hover:text-foreground"
        aria-label="帮助"
      >
        <InfoCircledIcon className="h-4 w-4" />
      </button>
    </HoverCardTrigger>
    <HoverCardContent align="start" className="z-[100] w-96 text-sm font-normal">
      <ul className="list-disc space-y-1.5 pl-4 leading-snug">
        <li>粘贴线上模版 ID（空格 / 逗号 / 换行分隔），先加载预览</li>
        <li>初始选中 = 这些模版的公共标签</li>
        <li>去掉某个公共标签 → 从全部所选模版里删除</li>
        <li>新加标签 → 给全部加上（已有的会跳过）</li>
        <li>某个模版独有、不在交集里的标签 → 不动</li>
        <li>会同步 Redis / Firestore frames / ES / templateExts / tagLibs</li>
      </ul>
    </HoverCardContent>
  </HoverCard>
)

export default function BatchOnlineFrameTagsTab() {
  const { toast } = useToast()
  const [rawIds, setRawIds] = useState('')
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [starting, setStarting] = useState(false)
  const [progressOpen, setProgressOpen] = useState(false)
  const [running, setRunning] = useState(false)
  const [job, setJob] = useState<BatchJob | null>(null)
  const pollRef = useRef<number | null>(null)

  const ids = useMemo(() => parseIds(rawIds), [rawIds])
  const foundFrames = useMemo(
    () => (preview?.frames || []).filter((f) => !f.missing),
    [preview],
  )
  const onlineFrames = useMemo(
    () => foundFrames.filter((f) => f.online),
    [foundFrames],
  )
  const commonTags = preview?.commonTags || getCommonTags(onlineFrames)
  const commonTagLevels = useMemo(
    () => splitTagsByLevel(commonTags),
    [commonTags],
  )

  const stopPolling = () => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  useEffect(() => () => stopPolling(), [])

  const resetProgressState = () => {
    stopPolling()
    setRunning(false)
    setJob(null)
  }

  const handleProgressOpenChange = (next: boolean) => {
    if (running) return
    setProgressOpen(next)
    if (!next) resetProgressState()
  }

  const loadPreview = async ({
    silentMissing = false,
  }: { silentMissing?: boolean } = {}) => {
    if (!ids.length) {
      toast({
        variant: 'destructive',
        title: '请先输入模版 ID',
      })
      return
    }
    setLoadingPreview(true)
    try {
      const data = (await postFetcher(
        '/ops-tools/online-frames/batch-tags/preview',
        { arg: { ids } },
      )) as PreviewResult
      setPreview(data)
      if (!silentMissing && data.totals.missing > 0) {
        toast({
          title: '部分 ID 未找到',
          description: `${data.totals.missing} 个不存在，将不会被更新`,
        })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: '预览失败',
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setLoadingPreview(false)
    }
  }

  const pollProgress = (nextJobId: string) => {
    stopPolling()
    pollRef.current = window.setInterval(async () => {
      try {
        const data = (await getFetcher(
          `/ops-tools/online-frames/batch-tags/jobs/${nextJobId}`,
        )) as BatchJob
        setJob(data)
        if (data.status === 'done') {
          stopPolling()
          setRunning(false)
          toast({
            title: '批量设置完成',
            description: `完成 ${data.done} · 跳过 ${data.skipped} · 失败 ${data.failed}`,
            className: 'bg-green-500 text-white',
          })
          void loadPreview({ silentMissing: true })
          setProgressOpen(false)
          resetProgressState()
        } else if (data.status === 'failed') {
          stopPolling()
          setRunning(false)
          toast({
            variant: 'destructive',
            title: '批量设置失败',
            description: `完成 ${data.done} · 失败 ${data.failed}`,
          })
          void loadPreview({ silentMissing: true })
        }
      } catch (err) {
        stopPolling()
        setRunning(false)
        toast({
          variant: 'destructive',
          title: '轮询任务失败',
          description: err instanceof Error ? err.message : String(err),
        })
      }
    }, 2000)
  }

  const handleConfirm = async (selected: string[]) => {
    const targetIds = onlineFrames.map((f) => f.id)
    if (!targetIds.length) {
      throw new Error('没有可更新的线上模版')
    }
    const commonSet = new Set(commonTags)
    const selectedSet = new Set(selected)
    const add = selected.filter((tag) => !commonSet.has(tag))
    const remove = commonTags.filter((tag) => !selectedSet.has(tag))
    if (!add.length && !remove.length) {
      toast({ title: '没有变更', description: '标签选择与当前公共标签相同' })
      throw new Error('no changes')
    }

    setStarting(true)
    try {
      const res = (await postFetcher('/ops-tools/online-frames/batch-tags', {
        arg: { ids: targetIds, add, remove },
      })) as { jobId: string }
      if (!res?.jobId) throw new Error('未返回 jobId')

      setJob({
        id: res.jobId,
        status: 'running',
        add,
        remove,
        totals: {
          frames: targetIds.length,
          ops: targetIds.length * (add.length + remove.length),
        },
        done: 0,
        failed: 0,
        skipped: 0,
        current: 'queued',
        errors: [],
      })
      setRunning(true)
      setProgressOpen(true)
      pollProgress(res.jobId)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: '提交失败',
        description: err instanceof Error ? err.message : String(err),
      })
      throw err
    } finally {
      setStarting(false)
    }
  }

  const progressTotal = job?.totals?.ops || 0
  const progressDone =
    (job?.done || 0) + (job?.failed || 0) + (job?.skipped || 0)
  const percent =
    progressTotal > 0
      ? Math.round((Math.min(progressDone, progressTotal) / progressTotal) * 100)
      : 0
  const statusLabel =
    job?.status === 'failed'
      ? '失败'
      : job?.status === 'done'
        ? '完成'
        : job?.current
          ? `处理中 · ${job.current}`
          : '处理中'

  return (
    <div className="w-full space-y-4">
      <div className="w-full space-y-4 rounded-lg border p-6">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">修改线上模板标签</h3>
          <Help />
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          针对已上架的 Avatar Frame：批量新增标签，或批量去掉公共标签。不会影响
          Spine / DLC。
        </p>

        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="frame-ids">
            模版 ID 列表
          </label>
          <Textarea
            id="frame-ids"
            value={rawIds}
            onChange={(e) => setRawIds(e.target.value)}
            placeholder="粘贴模版 ID，支持空格、逗号、换行分隔"
            className="min-h-[120px] w-full font-mono text-sm"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={() => void loadPreview()}
              disabled={loadingPreview || !ids.length}
            >
              {loadingPreview ? '加载中…' : `加载预览 (${ids.length})`}
            </Button>
            {preview ? (
              <span className="text-sm text-muted-foreground">
                找到 {preview.totals.found} / 线上 {preview.totals.online}
                {preview.totals.missing
                  ? ` / 缺失 ${preview.totals.missing}`
                  : ''}
              </span>
            ) : null}
          </div>
        </div>

        {onlineFrames.length > 0 ? (
          <div className="space-y-3">
            <div className="space-y-2">
              <span className="text-sm font-medium">公共标签</span>
              {commonTags.length ? (
                <div className="flex min-w-0 flex-wrap gap-1">
                  <TagBadges
                    tags={commonTagLevels.level1}
                    variant="default"
                  />
                  <TagBadges
                    tags={commonTagLevels.level2}
                    variant="outline"
                  />
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">无</span>
              )}
            </div>

            <TagPickerDialog
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              title={
                <span className="inline-flex items-center gap-2">
                  {`批量设置标签 (${onlineFrames.length})`}
                  <Help />
                </span>
              }
              initialTags={commonTags}
              selectionMode="multiple"
              confirmLabel="应用到所选模版"
              busy={starting}
              onConfirm={handleConfirm}
              trigger={
                <DialogTrigger asChild>
                  <Button type="button">
                    <TagsIcon className="mr-2 h-4 w-4" />
                    批量设置标签
                  </Button>
                </DialogTrigger>
              }
            />
          </div>
        ) : null}
      </div>

      <Dialog open={progressOpen} onOpenChange={handleProgressOpenChange}>
        <DialogContent className="dialog-content sm:max-w-md">
          <DialogHeader>
            <DialogTitle>批量设置标签</DialogTitle>
            <DialogDescription>
              正在逐条更新线上模版标签，请勿关闭页面。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3">
              {running ? <Loader className="h-6 w-6 text-primary" /> : null}
              <div className="min-w-0">
                <p className="text-sm font-medium">{statusLabel}</p>
                <p className="break-all text-sm text-muted-foreground">
                  完成 {job?.done ?? 0} · 跳过 {job?.skipped ?? 0} · 失败{' '}
                  {job?.failed ?? 0}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-200"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {progressDone}/{progressTotal || 0} · {percent}%
              </p>
            </div>

            {job?.add?.length || job?.remove?.length ? (
              <div className="flex flex-wrap gap-2 text-sm">
                {job.add?.map((tag) => (
                  <Badge key={`add-${tag}`} variant="default">
                    +{tag}
                  </Badge>
                ))}
                {job.remove?.map((tag) => (
                  <Badge key={`rm-${tag}`} variant="secondary">
                    -{tag}
                  </Badge>
                ))}
              </div>
            ) : null}

            {job?.errors?.length ? (
              <div className="max-h-40 space-y-1 overflow-y-auto rounded border bg-muted/30 p-3 text-xs">
                {job.errors.slice(0, 50).map((err, idx) => (
                  <div key={`${err.id}-${err.op}-${idx}`}>
                    {err.id} · {err.op}: {err.message}
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={running}
              onClick={() => handleProgressOpenChange(false)}
            >
              {running ? '处理中…' : '关闭'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {preview?.frames?.length ? (
        <div className="w-full space-y-3 rounded-lg border p-6">
          <h4 className="font-medium">模版预览</h4>
          <div className="max-h-[70vh] overflow-y-auto">
            <div className="sticky top-0 z-10 grid grid-cols-[96px_minmax(180px,240px)_minmax(0,1fr)] gap-4 border-b bg-background px-2 py-2 text-xs font-medium text-muted-foreground">
              <div>Cover</div>
              <div>ID / Name</div>
              <div>Tags</div>
            </div>
            <div className="divide-y">
              {preview.frames.map((frame) => {
                const { level1, level2 } = splitTagsByLevel(frame.tags)
                return (
                  <div
                    key={frame.id}
                    className="grid grid-cols-[96px_minmax(180px,240px)_minmax(0,1fr)] items-start gap-4 px-2 py-3"
                  >
                    <div className="shrink-0">
                      {frame.cover ? (
                        <MyImage
                          alt={frame.name || frame.id}
                          className="aspect-square h-24 w-24 rounded-md border border-border bg-black object-contain"
                          src={cdnUrl(frame.cover)}
                        />
                      ) : (
                        <div className="flex h-24 w-24 items-center justify-center rounded-md border border-border bg-muted text-xs text-muted-foreground">
                          N/A
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 space-y-1">
                      <div className="fw-bold break-words">
                        {frame.name || '—'}
                      </div>
                      <div className="break-all font-mono text-xs text-black/90">
                        {frame.id}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {frame.missing ? (
                          <Badge variant="destructive">missing</Badge>
                        ) : null}
                        {!frame.missing && !frame.online ? (
                          <Badge variant="secondary">offline</Badge>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex min-w-0 flex-wrap gap-1">
                      {!frame.tags?.length && !frame.missing ? (
                        <span className="text-xs text-muted-foreground">
                          无标签
                        </span>
                      ) : (
                        <>
                          <TagBadges tags={level1} variant="default" />
                          <TagBadges tags={level2} variant="outline" />
                        </>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
